// Read-only application projection. These local estimates authorize neither an
// application nor settlement; the legacy first-deposit claim API cannot submit it.
const safeCentsMax = BigInt(Number.MAX_SAFE_INTEGER);
const decimalFields = ['requiredAmount', 'bonusRate', 'fixedBonusAmount', 'maxBonusAmount', 'turnoverMultiplier'];
const unavailableTier = '活动档位暂不可用，请重新选择';

function decimal(value) {
  if (typeof value !== 'string' || value.length > 128 || !/^(0|[1-9]\d*)(?:\.\d+)?$/.test(value)) return null;
  const [whole, fraction = ''] = value.split('.');
  const trimmedFraction = fraction.replace(/0+$/, '');
  return {
    numerator: BigInt(whole + fraction), denominator: 10n ** BigInt(fraction.length),
    key: trimmedFraction ? `${whole}.${trimmedFraction}` : whole
  };
}

function safeCents(value) {
  return value >= 0n && value <= safeCentsMax ? Number(value) : null;
}

function amountCents(value) {
  // Form values are decimal strings; never coerce objects or already-rounded
  // floating-point money. Leading zeroes and surrounding input whitespace are OK.
  if (typeof value !== 'string') return null;
  const input = value.trim();
  if (input.length > 128 || !/^\d+(?:\.\d{1,2})?$/.test(input)) return null;
  const [whole, fraction = ''] = input.split('.');
  const cents = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
  return cents > 0n && cents <= safeCentsMax ? cents : null;
}

function configuration(tier) {
  if (!tier || !['FIXED', 'PERCENT'].includes(tier.rewardMode)) return null;
  const minimum = decimal(tier.requiredAmount);
  const reward = decimal(tier.rewardMode === 'PERCENT' ? tier.bonusRate : tier.fixedBonusAmount);
  const multiplier = decimal(tier.turnoverMultiplier);
  const capMissing = tier.maxBonusAmount == null || tier.maxBonusAmount === '';
  const cap = capMissing ? null : decimal(tier.maxBonusAmount);
  if (!minimum || !reward || !multiplier || (!capMissing && !cap)) return null;
  // An old threshold may have sub-cent precision; application input cannot.
  const minCents = safeCents((minimum.numerator * 100n + minimum.denominator - 1n) / minimum.denominator);
  if (minCents === null) return null;
  return { minimum, minCents, reward, multiplier, cap: cap?.numerator > 0n ? cap : null };
}

function configurationId(activityId, tier, rule) {
  // Content identity, not config-row-N or a backend tierId. Equivalent decimal
  // formatting, reordered rows and exact duplicate rules keep the same choice.
  return `local-deposit-config:${JSON.stringify([
    activityId, rule.minimum.key, tier.rewardMode, rule.reward.key,
    rule.cap?.key ?? null, rule.multiplier.key
  ])}`;
}

function option(activityId, tier) {
  const rule = configuration(tier);
  if (!rule) return null;
  const rewardLabel = tier.rewardMode === 'PERCENT' ? `${tier.bonusRate}%红利` : `固定奖励 ${tier.fixedBonusAmount} 元`;
  const capLabel = rule.cap ? `最高 ${tier.maxBonusAmount} 元` : '红利无上限';
  return {
    ...tier,
    id: tier.serverPlan === true ? tier.planId : configurationId(activityId, tier, rule),
    applicationConfiguration: true,
    label: `≥${tier.requiredAmount}元，${rewardLabel}，${capLabel}，${tier.turnoverMultiplier}倍流水`,
    minCents: rule.minCents,
    // No application maximum is present in the verified configuration.
    maxCents: null,
    percent: tier.rewardMode === 'PERCENT' ? tier.bonusRate : null,
    capCents: rule.cap ? safeCents(rule.cap.numerator * 100n / rule.cap.denominator) : null,
    hasBonusCap: rule.cap !== null,
    multiplier: tier.turnoverMultiplier
  };
}

/**
 * source is the verified settled deposit model from adaptCampaignModel.
 * rawTiers retains incomplete/read-only rows; tiers contains usable local choices.
 * percent/multiplier and the original rule fields stay exact decimal strings.
 * A null maxCents means no configured application maximum. A null capCents means
 * uncapped when hasBonusCap=false, or a cap beyond safe cents when true; raw cap
 * precision is always used for calculation. The source model is never mutated.
 */
export function createDepositApplicationModel(source, previousSelection = {}) {
  if (source?.kind !== 'deposit' || source.flow !== 'settled') {
    throw new TypeError('首存申请适配需要已验证的 settled 首存模型');
  }
  const rawTiers = Array.isArray(source.tiers) ? source.tiers.map(tier => ({ ...tier, ...(source.applicationSupported === true ? {serverPlan:true} : {}) })) : [];
  const seen = new Set();
  const tiers = rawTiers.map(tier => option(source.activityId, tier)).filter(tier => {
    if (!tier || seen.has(tier.id)) return false;
    seen.add(tier.id);
    return true;
  });
  // The verified projection already restricts venues to venueIdList.
  const venues = Array.isArray(source.venues) ? source.venues.map(venue => ({ ...venue })) : [];
  const selectedTier = tiers.find(tier => tier.id === previousSelection?.tierId) || tiers[0];
  const selectedVenue = venues.find(venue => venue.id === previousSelection?.venueId) || venues[0];
  return {
    ...source,
    rawTiers, tiers, venues,
    venueIdList: Array.isArray(source.venueIdList) ? [...source.venueIdList] : [],
    flow: 'application', applicationAvailable: source.applicationSupported === true && source.eligible === true && source.claimed === false, claimAvailable: false,
    estimateOnly: source.applicationSupported !== true,
    tierId: selectedTier?.id || '', venueId: selectedVenue?.id || '',
    selectedTierLabel: selectedTier?.label || '',
    instructions: '选择活动类型和场馆，填写申请金额，核对预估结果后确认申请。'
  };
}

/** Display only: Go decimal reward/cap, reward.Truncate(2), then turnover.Truncate(2). */
export function estimateDepositApplication(value, tier) {
  const amount = amountCents(value);
  const rule = configuration(tier);
  if (amount === null || !rule) return null;
  let numerator = tier.rewardMode === 'PERCENT' ? amount * rule.reward.numerator : rule.reward.numerator * 100n;
  let denominator = tier.rewardMode === 'PERCENT' ? rule.reward.denominator * 100n : rule.reward.denominator;
  if (rule.cap && numerator * rule.cap.denominator > rule.cap.numerator * 100n * denominator) {
    numerator = rule.cap.numerator * 100n;
    denominator = rule.cap.denominator;
  }
  const bonus = numerator / denominator;
  const turnover = (amount + bonus) * rule.multiplier.numerator / rule.multiplier.denominator;
  const bonusCents = safeCents(bonus);
  const turnoverCents = safeCents(turnover);
  if (bonusCents === null || turnoverCents === null) return null;
  return { amountCents: Number(amount), bonusCents, turnoverCents };
}

/** Empty string allows reviewing a draft, never a real business submission. */
export function validateDepositApplication(value, tier, venueId, model) {
  const selected = Array.isArray(model?.tiers) ? model.tiers.find(item => item?.id === tier?.id) : null;
  const rule = configuration(tier);
  // Reject stale/foreign/malformed options; display aliases cannot replace rules.
  if (model?.flow !== 'application' || !selected || tier?.applicationConfiguration !== true || !rule ||
    (tier.serverPlan === true ? !/^fdp1_[0-9a-f]{64}$/.test(tier.id) || tier.id !== selected.planId : configurationId(model.activityId, tier, rule) !== tier.id) ||
    !decimalFields.every(field => selected[field] === tier[field])) return unavailableTier;
  const amount = amountCents(value);
  if (amount === null) return '请输入有效金额，最多两位小数';
  if (!Array.isArray(model.venues) || !model.venues.some(venue => venue.id === venueId)) return '请选择活动场馆';
  if (amount < BigInt(rule.minCents)) return '金额不在当前档位范围内';
  if (!Number.isSafeInteger(model.balanceCents) || model.balanceCents < 0) return '中心钱包余额暂不可用，请稍后重试';
  if (model.applicationSupported === true) {
    if (!Number.isSafeInteger(model.applicationMaximumCents) || !Number.isSafeInteger(model.walletAvailableCents)) return '申请限额暂不可用，请刷新';
    if (amount > BigInt(model.applicationMaximumCents)) return '申请金额超过首笔到账或中心可用余额';
  }
  if (amount > BigInt(model.balanceCents)) return '中心钱包余额不足';
  if (!estimateDepositApplication(value, tier)) return '当前金额无法安全预估，请调整金额或稍后重试';
  // Old eligible/claimed receipts and final applicationAvailable are deliberately
  // irrelevant to opening a confirmation for this application draft.
  return '';
}
